import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LinkedinLogo, InstagramLogo, FacebookLogo, YoutubeLogo, TwitterLogo, MapPin, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { useI18n } from "../i18n/I18nContext";
import { useSiteContent } from "../i18n/SiteContent";
import ConnectDialog from "../widgets/ConnectDialog";

/** Split "a@b.com, c@d.com" or JSON array string into a clean array of strings. */
function parseList(raw) {
  if (!raw) return [];
  const s = String(raw).trim();
  if (!s) return [];
  if (s.startsWith("[")) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.map((x) => String(x).trim()).filter(Boolean);
    } catch { /* fall through */ }
  }
  return s.split(",").map((x) => x.trim()).filter(Boolean);
}

export default function Footer() {
  const { t } = useI18n();
  const { settings, pg, pgList } = useSiteContent();
  const [connectOpen, setConnectOpen] = useState(false);

  const address = settings?.address || "";
  // Primary + extra emails / phones
  const emails = Array.from(new Set([
    ...(settings?.email ? [settings.email] : []),
    ...(settings?.email_alt ? [settings.email_alt] : []),
    ...parseList(settings?.emails_extra),
  ])).filter(Boolean);
  const phones = Array.from(new Set([
    ...(settings?.phone ? [settings.phone] : []),
    ...parseList(settings?.phones_extra),
  ])).filter(Boolean);

  const social = [
    { key: "linkedin",  url: settings?.linkedin_url,  Icon: LinkedinLogo,  label: "LinkedIn" },
    { key: "instagram", url: settings?.instagram_url, Icon: InstagramLogo, label: "Instagram" },
    { key: "facebook",  url: settings?.facebook_url,  Icon: FacebookLogo,  label: "Facebook" },
    { key: "twitter",   url: settings?.twitter_url,   Icon: TwitterLogo,   label: "Twitter / X" },
    { key: "youtube",   url: settings?.youtube_url,   Icon: YoutubeLogo,   label: "YouTube" },
  ].filter((s) => (s.url || "").trim() && s.url !== "#");

  const desc = pg("footer.desc", t.footer.desc);
  const quickLinksLabel = pg("footer.quick_links_label", t.footer.quickLinks);
  const servicesLabel = pg("footer.services_label", t.footer.ourServices);
  const contactLabel = pg("footer.contact_label", t.footer.getInTouch);
  const rights = pg("footer.rights", t.footer.rights);
  const privacy = pg("footer.privacy", t.footer.privacy);
  const terms = pg("footer.terms", t.footer.terms);
  const navL = {
    about: pg("nav.about", t.nav.about),
    services: pg("nav.services", t.nav.services),
    industries: pg("nav.industries", t.nav.industries),
    products: pg("nav.products", "Products"),
  };
  const servicesGroups = pgList("services.groups", t.servicesMega.groups);
  const tagline = pg("brand.tagline", t.brand.tagline);
  const year = new Date().getFullYear();

  // All footer links use absolute `/#anchor` so they work from any page
  // (including product/service detail). Contact triggers the Connect dialog.
  const LINKS = [
    { id: "about",      label: navL.about,      href: "/who-we-are" },
    { id: "products",   label: navL.products,   href: "/#products" },
    { id: "services",   label: navL.services,   href: "/#services" },
    { id: "industries", label: navL.industries, href: "/#industries" },
    { id: "faq",        label: "FAQs",          href: "/#faq" },
    { id: "portfolio",  label: "Clients Portfolio", href: "/clients-portfolio" },
    { id: "contact",    label: "Contact",       onClick: () => setConnectOpen(true) },
  ];

  return (
    <footer className="text-white" style={{ background: "#0F2A6E" }} data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img src="/logo.png" alt="OpSysDigital" className="h-14 w-auto brightness-0 invert mb-3" />
            {tagline && <div className="text-[10.5px] uppercase tracking-[0.15em] text-slate-400">{tagline}</div>}
            {desc && <p className="mt-4 text-[13.5px] text-slate-300 leading-relaxed font-light max-w-md">{desc}</p>}
          </div>

          {/* Links */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-4">{quickLinksLabel}</div>
            <ul className="space-y-2 text-[13.5px]">
              {LINKS.map((lnk) => (
                <li key={lnk.id}>
                  {lnk.onClick ? (
                    <button
                      type="button"
                      data-testid={`footer-link-${lnk.id}`}
                      onClick={lnk.onClick}
                      className="text-slate-300 hover:text-[#1B6FE0] transition text-left"
                    >
                      {lnk.label}
                    </button>
                  ) : (
                    <Link
                      to={lnk.href}
                      data-testid={`footer-link-${lnk.id}`}
                      className="text-slate-300 hover:text-[#1B6FE0] transition"
                    >
                      {lnk.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          {servicesGroups.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-4">{servicesLabel}</div>
              <ul className="space-y-2 text-[13.5px]">
                {servicesGroups.map((g, i) => (
                  <li key={i}>
                    <Link to="/#services" className="text-slate-300 hover:text-[#1B6FE0] transition">{g.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Get in touch */}
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-4">{contactLabel}</div>
            <ul className="space-y-3 text-[13.5px] text-slate-300">
              {address && (
                <li className="flex items-start gap-2"><MapPin size={13} weight="bold" className="mt-0.5 text-[#1B6FE0]" /><span>{address}</span></li>
              )}
              {emails.map((em) => (
                <li key={em} className="flex items-center gap-2">
                  <EnvelopeSimple size={13} weight="bold" className="text-[#1B6FE0]" />
                  <a href={`mailto:${em}`} className="hover:text-white transition">{em}</a>
                </li>
              ))}
              {phones.map((ph) => (
                <li key={ph} className="flex items-center gap-2">
                  <Phone size={13} weight="bold" className="text-[#1B6FE0]" />
                  <a href={`tel:${ph.replace(/\s/g, "")}`} className="hover:text-white transition">{ph}</a>
                </li>
              ))}
            </ul>

            {social.length > 0 && (
              <div className="flex items-center gap-2 mt-6">
                {social.map(({ key, url, Icon, label }) => (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`social-${key}`}
                     className="w-9 h-9 rounded-md border border-white/15 flex items-center justify-center text-slate-300 hover:text-white hover:bg-[#1B6FE0] hover:border-[#1B6FE0] transition">
                    <Icon size={15} weight="regular" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[11.5px] text-slate-400">
          <div>© {year} OpSysDigital. {rights}</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-white transition">{privacy}</a>
            <a href="#" className="hover:text-white transition">{terms}</a>
          </div>
        </div>
      </div>
      <ConnectDialog open={connectOpen} onOpenChange={setConnectOpen} />
    </footer>
  );
}
